import SimpleHeader from '../../components/layout/SimpleHeader';
import { Table, TableColumnsType } from 'antd';
import { useEffect, useState } from 'react';
import config from '../../lib/config';
import Button from '../../components/Button';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import Pagination from '../../components/Pagination';
import styled from 'styled-components';
import useAxios from '../../hooks/useAxios';
import {
  IGeneralMessageCustomer,
  IGetMessageDetails,
  IGetMessageReSend,
  IMessageDetail,
  IStudent,
} from '../../models/pushMessage.model';
import {
  cancelReservation,
  getMessageDetails,
  getMessageResend,
} from '../../api/pushMessage.api';
import Loader from '../../components/Loader';
import ErrorResponse from '../../components/ErrorResponse';
import moment from 'moment';
import Radio from '../../components/Radio';
import ConfirmModal from '../../components/modals/ConfirmModal';
import useNotification from '../../hooks/useNotification';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setCustomers, setStudents } from '../../store/pushMessage.slice';

const Block = styled.div`
  .ant-table-wrapper {
    margin-top: 0;
  }
`;

interface IMatch {
  smsSendCode: string;
}

const SmsDetail = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch<IMatch>();
  const dispatch = useDispatch();
  const { successNotification } = useNotification();
  const smsSendCode = Number(match.params.smsSendCode);
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetMessageDetails>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetMessageDetails>();
  const {
    data: res3,
    isLoading: isLoading3,
    error: error3,
    execute: execute3,
  } = useAxios<IGetMessageReSend>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [sendType, setSendType] = useState(
    history.location.pathname.includes('nonmember') ? 'ME' : 'AP',
  );
  const [data, setData] = useState<IMessageDetail[]>([]);
  const [page, setPage] = useState(1);
  const [confirmCancelVisible, setConfirmCancelVisible] = useState(false);

  useEffect(() => {
    if (smsSendCode) {
      execute(getMessageDetails(smsSendCode, { sendType: 'AP' }));
      execute2(getMessageDetails(smsSendCode, { sendType: 'ME' }));
      execute3(getMessageResend(smsSendCode));
    }
  }, [smsSendCode]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      setConfirmCancelVisible(false);
      successNotification(t('common.message.cancel'));
      history.location.pathname.includes('nonmember')
        ? history.push('/sms/nonmember')
        : history.push('/sms/member');
    }
  }, [submitRes, submitIsLoading]);

  useEffect(() => {
    if (!res || !res2) return;
    if (sendType === 'AP') {
      setData(res.data);
    } else {
      setData(res2?.data);
    }
  }, [sendType, res, res2]);

  const getSuccessCount = () => {
    if (!res || !res2) return 0;
    const data1 = res.data.filter(
      (item) =>
        item.status === '06' || item.status === 'Y' || item.status === '1000',
    );
    const data2 = res2.data.filter(
      (item) =>
        item.status === '06' || item.status === 'Y' || item.status === '1000',
    );
    return data1.length + data2.length;
  };

  const getReserveCount = () => {
    if (!res || !res2) return 0;
    const data1 = res.data.filter((item) => item.status === '0');
    const data2 = res2.data.filter((item) => item.status === '0');
    return data1.length + data2.length;
  };

  const getFailCount = () => {
    if (!res || !res2) return 0;
    return (
      (res?.data.length || 0) +
      (res2?.data.length || 0) -
      getSuccessCount() -
      getReserveCount()
    );
  };

  const getSendType = () => {
    if (!res || !res2 || (res.data.length === 0 && res2.data.length === 0)) {
      return '';
    }
    let sendType = '';

    if (res.data.length > 0) {
      sendType = res.data[0].sendType;
    } else {
      sendType = res2.data[0].sendType;
    }

    switch (sendType) {
      case 'GE':
        return t('student.student.option20');
      case 'AD':
        return t('student.student.option21');
      default:
        return sendType;
    }
  };

  const getSendTimeDiv = () => {
    if (!res || !res2 || (res.data.length === 0 && res2.data.length === 0)) {
      return '';
    }
    let sendTimeDiv = '';

    if (res.data.length > 0) {
      sendTimeDiv = res.data[0].sendTimeDiv;
    } else {
      sendTimeDiv = res2.data[0].sendTimeDiv;
    }

    switch (sendTimeDiv) {
      case 'IM':
        return t('student.student.option22');
      case 'RE':
        return t('student.student.option23');
      case 'CN':
        return t('sms.list.option2');
      default:
        return sendTimeDiv;
    }
  };

  const getSendDate = () => {
    if (!res || !res2 || (res.data.length === 0 && res2.data.length === 0)) {
      return '-';
    }
    let sendDate = '';

    if (res.data.length > 0) {
      sendDate = res.data[0].sendDate;
    } else {
      sendDate = res2.data[0].sendDate;
    }

    return sendDate ? moment(sendDate).format('YYYY.MM.DD HH:mm') : '-';
  };

  const getTitle = () => {
    if (!res || !res2 || (res.data.length === 0 && res2.data.length === 0)) {
      return '';
    }

    if (res.data.length > 0) {
      return res.data[0].title;
    } else {
      return res2.data[0].title;
    }
  };

  const onSetSendType = (sendType: string) => {
    setSendType(sendType);
    setPage(1);
  };

  const onCancelReservation = () => {
    submitExecute(cancelReservation(smsSendCode));
  };

  const onReSend = () => {
    const _total = [...(res?.data ?? []), ...(res2?.data ?? [])];
    if (res3?.data.sendTarget === 'ME') {
      const _students = _total.map(({ memberCode, memberName, memberEngName, webId, phone, levelName, className }) => {
          return {
            memberCode,
            memberName,
            memberEngName,
            webId,
            mobilePhone: phone,
            courseName: levelName,
            className,
          } as IStudent;
        },
      );
      dispatch(
        setStudents({ content: res3?.data.content, students: _students }),
      );
      history.push('/sms/member/send');
    } else {
      const _customers = _total.map(({ phone, memberCode }) => {
        return { phone, name: memberCode } as IGeneralMessageCustomer;
      });
      dispatch(
        setCustomers({
          content: res3?.data.content ?? '',
          customers: _customers,
        }),
      );
      history.push('/sms/nonmember/send');
    }
  };

  const columns: TableColumnsType<IMessageDetail> = [
    {
      title: 'No.',
      align: 'center',
      render: (value, record, index) => index + 1,
    },
    {
      title: t('sms.list.column5'),
      dataIndex: 'senderMemberName',
      key: 'senderMemberName',
      align: 'center',
      render: (value, record) => `${value} (${record.senderWebId})`,
    },
    {
      title: t('sms.list.column12'),
      dataIndex: 'memberName',
      key: 'memberName',
      align: 'center',
      render: (value, record) => {
        if (record.memberName === null) {
          return record.phone;
        } else {
          return `${value} (${record.webId})`;
        }
      },
    },
    {
      title: t('student.student.column4'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (value) => {
        switch (value) {
          case '06':
          case 'Y':
          case '1000':
            return `${t('sms.list.column7')}(${value})`;
          case '0':
            return `${t('student.student.option14')}(0)`;
          default:
            return `${t('sms.list.column8')}(${value})`;
        }
      },
    },
  ];

  return (
    <Block className="content scrollbar">
      {isLoading || isLoading2 || isLoading3 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={submitError} />
      <SimpleHeader
        parentMenu={t('common.menu.text')}
        currentMenu={t('sms.list.title1')}
      />      
      
      <div className='comm-table__area'>
        <table>
          <colgroup>
            <col width="14%" />
            <col width="14%" />
            <col width="14%" />
            <col width="14%" />
            <col width="14%" />
            <col width="14%" />
            <col width="*" />
          </colgroup>
          <thead>
            <tr>
              <th className="text-center">{t('sms.list.column6')}</th>
              <th className="text-center">{t('sms.list.column7')}</th>
              <th className="text-center">{t('sms.list.column8')}</th>
              <th className="text-center">{t('student.student.option14')}</th>
              <th className="text-center">{t('sms.list.column2')}</th>
              <th className="text-center">{t('sms.list.column3')}</th>
              <th className="text-center">{t('sms.list.column9')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="text-center">
                {(res?.data.length || 0) + (res2?.data.length || 0)}
              </td>
              <td className="text-center">{getSuccessCount()}</td>
              <td className="text-center">{getFailCount()}</td>
              <td className="text-center">{getReserveCount()}</td>
              <td className="text-center">{getSendType()}</td>
              <td className="text-center">{getSendTimeDiv()}</td>
              <td className="text-center">{getSendDate()}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="row">
        <div className="col-7">
          <div className='comm-table__area rowType'>
            <table>
              <colgroup>
                <col width="25%" />
                <col width="75%" />
              </colgroup>
              <tbody>
                <tr>
                  <th className="text-center">{t('sms.list.column14')}</th>
                  <td>
                    <div className='radio-area'>
                    <Radio
                      checked={sendType === 'ME'}
                      onChange={() => onSetSendType('ME')}
                      text={t('sms.list.column10')}
                    />
                    <Radio
                      className='ml-4'
                      checked={sendType === 'AP'}
                      onChange={() => onSetSendType('AP')}
                      text={t('sms.list.column11')}
                    />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className='comm-table__area'>
            <Table
              columns={columns}
              dataSource={data}
              pagination={{
                current: page,
                pageSize: config.pageSize,
              }}
              onRow={(record) => {
                return {
                  onClick: () => {},
                };
              }}
              rowKey="messageCode"
            />
          </div>
          <div className="comm-bx__between">
            <div className="comm-btn__area">
              <Button
                className="btn-basic gray-line"
                onClick={() => {
                  const pathArr = location.pathname.split('/');
                  history.push(`/sms/${pathArr[2]}${location.search}`);
                }}
                text={t('common.button.back')}
              />
              
            </div>
            <div className="common-paging center">
              <Pagination count={data.length} page={page} setPage={setPage} />
            </div>
            <div className="comm-btn__area">
              {res3?.data.reusePsbYN === 'Y' && (
                <Button
                  className="btn-basic pink-line"
                  onClick={onReSend}
                  text={t('sms.list.button1')}
                />
              )}
              {
                (getSendTimeDiv() === t('student.student.option23') && moment().isBefore(getSendDate()))
                  ? (
                    <Button
                      className="btn-basic gray"
                      onClick={() => setConfirmCancelVisible(true)}
                      text={t('sms.list.option2')}
                    />
                    )
                  : null
              }
            </div>
          </div>
        </div>
        <div className="col-5">
          <div className='comm-table__area rowType edit'>
            <table>
              <tbody>
                <tr>
                  <th className="text-center">{t('sms.list.column13')}</th>
                </tr>
                <tr>
                  <td className="text-center">
                    <input
                      type="text"
                      className="form-control input-text"
                      value={getTitle()}
                      placeholder={`(${t('sms.list.placeholder1')})`}
                      readOnly
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <textarea
                      className="w-100"
                      rows={10}
                      value={res3?.data.content.replaceAll('|', '\n').trim()}
                      readOnly
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {confirmCancelVisible && (
        <ConfirmModal
          visible={confirmCancelVisible}
          setVisible={setConfirmCancelVisible}
          onConfirm={onCancelReservation}
          titleText={t('sms.list.option2')}
          messageText={t('sms.list.message2')}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />
      )}
    </Block>
  );
};

export default SmsDetail;
